// @flow
import moment from 'moment'
import ONDEMAND_STATUS from '../../../constants/ondemandStatus'
import PRICE_TYPE from '../../../constants/priceType'
import { isCancelationFree } from '../../../models/step'
import searchPriceBuilder from '../../trips/builders/tripPrice'
import buildRideLocation from '../../trips/builders/rideLocation'
import { rideTrackingAmenity } from '../../trips/builders/tripAmenity'

// Types
import type { Step } from '../../../models/step'
import type { Reservation } from '../../../models/reservation'
import type { OndemandReservation } from '../../../api_types'

/**
 * Returns true if the on-demand trip cancelled in any way
 * @param  {string}  status
 * @return {Boolean}
 */
function isCancelled(status) {
  switch (status) {
    case ONDEMAND_STATUS.CANCELLED_BY_USER:
    case ONDEMAND_STATUS.CANCELLED_BY_DRIVER:
      return true
    default:
      return false
  }
}

function buildOndemandStep(response: OndemandReservation): Step {
  const { ride } = response
  const startLocation = ride.start_location
    ? buildRideLocation(ride.start_location)
    : null
  const endLocation = ride.end_location
    ? buildRideLocation(ride.end_location)
    : null
  const rawPickupDatetime =
    response.pickup_datetime || response.status_timestamp.pending
  const departureDatetime = moment.parseZone(rawPickupDatetime || undefined)
  const accurateTime =
    ((ride.estimates && ride.estimates.duration_seconds) || 0) / 60
  const arrivalDatetime = departureDatetime
    .clone()
    .add(accurateTime || 30, 'minutes')
  const relevantPrice = ride.cost || ride.estimates || {}
  const cancelPenalty = relevantPrice.cancel_penalty_amount
  const currencyObj = relevantPrice.currency || {}
  const waitTime = parseInt((ride.cost && ride.cost.waiting_minute) || '0', 10)
  const penaltyValue = (cancelPenalty && parseFloat(cancelPenalty.value)) || 0
  const cancellationPolicy = []

  if (relevantPrice && cancelPenalty && penaltyValue > 0) {
    cancellationPolicy.push({
      notice: 1,
      refundPercent: 0,
      penaltyPrice: {
        total: cancelPenalty.compact,
        slash: '',
        raw: penaltyValue,
        currencyCode: currencyObj.code,
        currencySymbol: currencyObj.sign,
        type: PRICE_TYPE.FIXED_PRICE,
      },
    })
  }

  return {
    type: 'car',
    from: {
      location: startLocation,
      distance: 0,
    },
    to: {
      location: endLocation,
      distance: 0,
    },
    description: ride.description || '',
    main: true,
    alternativeTimesIndex: -1,
    alternativeTimes: [],
    serviceHours: {},
    ticketTypes: [],
    passTypes: [],
    arrivalTimes: [],
    arrivalDatetime,
    departureDatetime,
    bookingDetails: {
      gratuityIncluded: true,
      tollsIncluded: true,
      meetAndGreetDefault: false,
      meetAndGreetAvailable: false,
      meetAndGreetPrice: '',
      meetAndGreetRawPrice: 0,
      cancellationPolicy,
      cancellableOffline: false, // TODO check
      cancellableOnline: true,
      amendable: true,
      waitTime,
      extraPaxRequired: false,
      acceptsGratuity: false,
      showFlightInfo: false, // TODO check
      areTermsInternal: false,
      termsOfServiceURL: '',
      notes: '',
      isBookable: true,
      startTime: null,
      endTime: null,
      showTollsAndGratuity: false,
      startAddressIncomplete: false,
      endAddressIncomplete: false,
      specialInstructionsPlaceholder: '',
      hourlyDetails: null,
    },
    vehicle: {
      type: {
        id: ride.search_vehicle.vehicle_type.key,
        key: ride.search_vehicle.vehicle_type.name,
        name: ride.search_vehicle.vehicle_type.name,
      },
      maxPassengers: ride.search_vehicle.max_passengers,
      maxBags: ride.search_vehicle.max_bags,
      isMaxBagsPerPerson: ride.search_vehicle.is_max_bags_per_person,
      imageUrl: ride.vehicle?.image_url || ride.provider.logo || '', // eslint-disable-line
      numVehicles: ride.search_vehicle.num_vehicles || 1,
      totalBags: ride.search_vehicle.total_bags,
      make: ride.search_vehicle.make || '',
      model: ride.search_vehicle.model || '',
      vehicleClass: ride.search_vehicle.vehicle_class_detail?.vehicle_class_id, // eslint-disable-line camelcase
      vehicleClassName:
        ride.search_vehicle.vehicle_class_detail?.display_name || '', // eslint-disable-line camelcase
    },
    provider: {
      logoUrl: ride.provider.logo || ride.vehicle?.image_url || '', // eslint-disable-line
      name: ride.provider.display_name || ride.provider.name || '',
      realName: ride.provider.name || ride.provider.display_name || '',
      email: '',
      phoneNumber: '',
      rating: 0,
      ratingFloat: 0,
      ratingCount: 0,
      supplierScore: null,
    },
    amenities: [rideTrackingAmenity],
    passengersCount: response.num_passengers,
    accurateTime,
  }
}

/**
 * Returns a Reservation object
 * @param  {Object} reservation Reservation response
 * @return {Object} Parsed reservation object
 * @private
 */
function ondemandReservationBuilder(
  reservation: OndemandReservation
): Reservation {
  const { ride } = reservation
  const step = buildOndemandStep(reservation)
  const policyWithPenalty = step.bookingDetails.cancellationPolicy.find(
    (x) => !!x.penaltyPrice
  )
  const cancelPenalty =
    (policyWithPenalty && policyWithPenalty.penaltyPrice) || null
  const totalPrice = searchPriceBuilder({
    price: ride.fixed,
    estimates: ride.estimates,
    cost: ride.cost,
  })

  return {
    trip: {
      id: reservation.id,
      searchId: '',
      type: 'ondemand',
      totalPrice: totalPrice.raw,
      tags: [],
      duration: step.accurateTime || 0,
      startLocation: step.from.location,
      endLocation: step.to.location,
      createdAt: moment.parseZone(
        reservation.status_timestamp[ONDEMAND_STATUS.PENDING]
      ),
      expiresAt: +Infinity,
      expiresIn: +Infinity,
      numPassengers: reservation.num_passengers,
      pickupDatetime: step.departureDatetime,
      flightDatetime: null,
      returnFlightDatetime: null,
      returnPickupDatetime: null,
      rewardPoints: 0,
      rewardProgram: null,
      rewardProgramDisplay: null,
      flightType: '',
      isEstimate: null,
      preferred: false,
      preferredByCompany: false,
      recommended: false,
      freeCancellation: isCancelationFree([step]),
      taxesAndFeesIncluded: true,
      outOfPolicy: false,
      vehiclesIncluded: [],
      steps: [step],
      returnSteps: [],
      discountPercentage: null,
      price: totalPrice,
      confirmationNumber: reservation.user_external_id,
      cancelled: isCancelled(reservation.status),
      specialInstructions: reservation.customer_notes || '',
      extraPaxInfo: [],
      owner: {
        firstName: reservation.passenger_information.first_name,
        lastName: reservation.passenger_information.last_name,
        phoneNumber: reservation.passenger_information.phone_national_number,
        countryCodeName: reservation.passenger_information.phone_country_code,
        email: reservation.passenger_information.email,
      },
    },
    canCancel: !!reservation.can_cancel,
    pickupInstructions: ride.pickup_instructions || '',
    pickupTime: step.departureDatetime,
    amountPaid: '',
    amendable: false,
    cancellableOffline: false,
    cancellableOnline: !!reservation.can_cancel,
    refundPercent: 0,
    cancellationPenaltyPrice: cancelPenalty,
    meetAndGreetAvailable: false,
    cancellationPolicyNotice: 0,
    userType: reservation.user_type,
    partnerTrackingId: reservation.partner_tracking_id,
    mozioProfitUsd: '',
    partnerProfitUsd: '',
    grossRevenueUsd: '',
    mozioProfit: '',
    partnerProfit: '',
    grossRevenue: '',
    extraTripOffer: null,
    initiatedBy: null,
  }
}

export default ondemandReservationBuilder
